import { ChevronDownIcon } from "@chakra-ui/icons";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Box,
    Center,
    useDisclosure,
    Text,
    HStack,
    Input,
    useBoolean,
    Select,
} from "@chakra-ui/react";
import {
    getDeliveryDelay,
    getDeliveryTimeSlot,
    getHoliDay,
    getPickupDelay,
    getPickupTimeSlot,
    getTimeSlotTextByTime,
} from "assets/data/holiday";
import { convertDateYYYYMMDDF } from "lib/utils/regular";
import { useEffect, useState } from "react";
import SelectBtn from "routes/Order/components/SelectBtn";
import { scheduleParams } from "routes/Order/OrderSheet";

interface ScheduleRecordProps {
    reservationType: "pickup" | "delivery";
    data?: scheduleParams;
    isOpen: boolean;
    callback: (data?: scheduleParams) => void;
}

const ScheduleRecordModal = (props: ScheduleRecordProps) => {
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [dateAry, setDateAry] = useState<Date[]>([]);
    const timeSlotAry: string[] = getPickupTimeSlot();

    const [face, setFace] = useState<boolean>(true);
    const [pickupDate, setPickupDate] = useState<string>();
    const [pickupTime, setPickupTime] = useState<string>();
    const [isActiveBtn, setIsActiveBtn] = useBoolean(false);

    const initSchedule = () => {
        const dateList: Date[] = [];

        const toDay = new Date();
        const presentDate = new Date(
            toDay.getFullYear(),
            toDay.getMonth(),
            toDay.getDate() + 1
        );

        // const delayDayCount = getPickupDelay();
        // const firstTimeSlot = getPickupTimeSlot()[0];

        const delayDayCount =
            props.reservationType === "pickup"
                ? getPickupDelay()
                : getDeliveryDelay();

        const firstTimeSlot =
            props.reservationType === "pickup"
                ? getPickupTimeSlot()[0]
                : getDeliveryTimeSlot()[0];

        let currentDate = new Date(presentDate);
        currentDate.setHours(Number.parseInt(firstTimeSlot), 0, 0, 0);

        let spliceIndex = 0;
        do {
            const holiDayAry: Number[] =
                getHoliDay()[currentDate.getFullYear()][
                    currentDate.getMonth() + 1
                ];

            const every = holiDayAry.every(
                (holi) => currentDate.getDate() !== holi
            );

            if (every) {
                dateList.push(new Date(currentDate));

                const currentYMD = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    currentDate.getDate()
                ).getTime();

                if (currentYMD < presentDate.getTime()) {
                    spliceIndex += 1;
                }
            }

            currentDate.setDate(currentDate.getDate() + 1); // 내일
        } while (dateList.length < 7 + delayDayCount);

        dateList.splice(0, spliceIndex + delayDayCount);
        setDateAry(dateList);

        const { data } = props;
        if (data) {
            setFace(data.isFace);
            const fixedDate = new Date(Number.parseInt(data.date));
            const fixedTime = fixedDate.getHours().toString().padStart(2, "0");
            setPickupTime(fixedTime);

            fixedDate.setHours(Number.parseInt(firstTimeSlot), 0, 0, 0);

            setPickupDate(fixedDate.getTime().toString());
        } else {
            setFace(true);
            setPickupDate(undefined);
            setPickupTime(undefined);
        }
    };

    const checkActiveBtn = () => {
        setIsActiveBtn.off();
        if (!pickupDate) return;
        if (!pickupTime) return;
        setIsActiveBtn.on();
    };

    const handleComplete = () => {
        if (!pickupDate || !pickupTime) return;

        // pick date object
        let pickDate = new Date(Number.parseInt(pickupDate));
        pickDate.setHours(Number.parseInt(pickupTime));

        const data: scheduleParams = {
            isFace: face,
            date: `${pickDate.getTime()}`,
        };
        if (props) {
            props.callback(data);
        }
        onClose();
    };

    const changeSchedule = (isFace: boolean) => {
        setFace(isFace);
    };

    const onClickClose = () => {
        if (props) {
            props.callback(undefined);
        }
    };

    const onChangeDate = (timeStamp: string) => {
        console.log("onchangedate : ", timeStamp);
        setPickupDate(timeStamp);
    };

    const onChangeTime = (time: string) => {
        console.log("onChangeTime : ", time);
        setPickupTime(time);
    };

    useEffect(() => {
        if (props.isOpen) {
            initSchedule();
            onOpen();
        } else {
            onClose();
        }
    }, [props.isOpen]);

    useEffect(() => {
        checkActiveBtn();
    }, [pickupDate, pickupTime]);

    return (
        <Modal isOpen={isOpen} onClose={onClickClose}>
            <ModalOverlay>
                <ModalContent w="19.6875rem">
                    <ModalHeader fontSize="lg" fontWeight="bold">
                        <Box position={"relative"} my="0.5rem" h="1.5rem">
                            <Center fontSize={"1.125rem"} fontWeight="semibold">
                                픽업/배송 일시 예약
                            </Center>
                            <ModalCloseButton
                                right={0}
                                top={0}
                                w={"1.5rem"}
                                height={"1.5rem"}
                                onClick={onClickClose}
                            />
                        </Box>
                    </ModalHeader>
                    <ModalBody p={"1rem"}>
                        <Text
                            fontSize={"0.813rem"}
                            fontWeight="semibold"
                            mb={"0.5rem"}
                        >
                            방문 방식
                        </Text>
                        <HStack mb="1.5rem">
                            <SelectBtn
                                borderWidth={face ? "2px" : "1px"}
                                borderColor={face ? "mint.500" : "#cfcfcf"}
                                bgColor={face ? "#f9fefb" : "white"}
                                color={"black"}
                                callbackOnClick={() => changeSchedule(true)}
                                label={"대면"}
                            />
                            <SelectBtn
                                borderWidth={!face ? "2px" : "1px"}
                                borderColor={!face ? "mint.500" : "#cfcfcf"}
                                bgColor={!face ? "#f9fefb" : "white"}
                                color={"black"}
                                callbackOnClick={() => changeSchedule(false)}
                                label={"비대면"}
                            />
                        </HStack>
                        <Text
                            fontSize={"0.813rem"}
                            fontWeight="semibold"
                            mb={"0.5rem"}
                        >
                            픽업/배송 일자
                        </Text>
                        <Select
                            h="3.375rem"
                            fontSize={"0.938rem"}
                            fontWeight="medium"
                            mb="1.5rem"
                            focusBorderColor={"gray.100"}
                            borderRadius="sm"
                            color={pickupDate ? "black" : "#bdbdbd"}
                            placeholder="픽업/배송 일자를 선택해 주세요."
                            icon={<ChevronDownIcon />}
                            value={pickupDate}
                            onChange={(v) => onChangeDate(v.target.value)}
                        >
                            {dateAry.map((v, index) => (
                                <option
                                    key={`pickdate_${index}`}
                                    value={v.getTime()}
                                >
                                    {convertDateYYYYMMDDF(v.getTime())}
                                </option>
                            ))}
                        </Select>
                        <Text
                            fontSize={"0.813rem"}
                            fontWeight="semibold"
                            mb={"0.5rem"}
                        >
                            픽업/배송 시간
                        </Text>
                        <Select
                            h="3.375rem"
                            fontSize={"0.938rem"}
                            fontWeight="medium"
                            focusBorderColor={"gray.100"}
                            borderRadius="sm"
                            color={pickupTime ? "black" : "#bdbdbd"}
                            placeholder="픽업/배송 시간을 선택해 주세요."
                            icon={<ChevronDownIcon />}
                            value={pickupTime}
                            onChange={(v) => onChangeTime(v.target.value)}
                        >
                            {timeSlotAry.map((v, index) => (
                                <option key={`picktime_${index}`} value={v}>
                                    {getTimeSlotTextByTime(v)}
                                </option>
                            ))}
                        </Select>
                    </ModalBody>
                    <ModalFooter
                        p={"1rem"}
                        maxW={"768px"}
                        minW={"320px"}
                        w="100%"
                        h="86px"
                    >
                        <Input
                            w="100%"
                            h={"100%"}
                            fontSize={"1rem"}
                            fontWeight="semibold"
                            bg={isActiveBtn ? "#5bbca6" : "#e0e0e0"}
                            color={"#fff"}
                            type="submit"
                            onClick={handleComplete}
                            value="예약하기"
                        />
                    </ModalFooter>
                </ModalContent>
            </ModalOverlay>
        </Modal>
    );
};

export default ScheduleRecordModal;
